import {useCallback, useRef, type PointerEvent as ReactPointerEvent} from 'react'
import type {CropRect} from '../../../common/utils/imageCrop'

interface ImageCropOverlayProps {
  rect: CropRect
  onRectChange: (rect: CropRect) => void
}

type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se'

interface DragState {
  mode: DragMode
  startX: number
  startY: number
  startRect: CropRect
  containerW: number
  containerH: number
}

const MIN_SIZE = 0.05
const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))

const CORNERS: DragMode[] = ['nw', 'ne', 'sw', 'se']

/** Drag-to-select crop rectangle overlaid on the preview canvas. Rendered inside
 * a container that exactly matches the canvas's rendered box (see
 * ImagePreviewCanvas.tsx's wrapper), so CSS percentages map 1:1 to fractions of
 * the image's natural size regardless of on-screen zoom level. */
const ImageCropOverlay = ({rect, onRectChange}: ImageCropOverlayProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)

  const beginDrag = useCallback(
    (mode: DragMode) => (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const container = containerRef.current
      if (!container) return
      const box = container.getBoundingClientRect()
      dragRef.current = {
        mode,
        startX: e.clientX,
        startY: e.clientY,
        startRect: rect,
        containerW: box.width,
        containerH: box.height,
      }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [rect],
  )

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (!drag) return
      const dxFrac = drag.containerW > 0 ? (e.clientX - drag.startX) / drag.containerW : 0
      const dyFrac = drag.containerH > 0 ? (e.clientY - drag.startY) / drag.containerH : 0
      const s = drag.startRect

      if (drag.mode === 'move') {
        onRectChange({
          ...s,
          x: clamp(s.x + dxFrac, 0, 1 - s.w),
          y: clamp(s.y + dyFrac, 0, 1 - s.h),
        })
        return
      }

      let {x, y, w, h} = s
      if (drag.mode === 'se') {
        w = clamp(s.w + dxFrac, MIN_SIZE, 1 - s.x)
        h = clamp(s.h + dyFrac, MIN_SIZE, 1 - s.y)
      } else if (drag.mode === 'nw') {
        const newX = clamp(s.x + dxFrac, 0, s.x + s.w - MIN_SIZE)
        const newY = clamp(s.y + dyFrac, 0, s.y + s.h - MIN_SIZE)
        w = s.x + s.w - newX
        h = s.y + s.h - newY
        x = newX
        y = newY
      } else if (drag.mode === 'ne') {
        const newY = clamp(s.y + dyFrac, 0, s.y + s.h - MIN_SIZE)
        w = clamp(s.w + dxFrac, MIN_SIZE, 1 - s.x)
        h = s.y + s.h - newY
        y = newY
      } else if (drag.mode === 'sw') {
        const newX = clamp(s.x + dxFrac, 0, s.x + s.w - MIN_SIZE)
        w = s.x + s.w - newX
        h = clamp(s.h + dyFrac, MIN_SIZE, 1 - s.y)
        x = newX
      }
      onRectChange({x, y, w, h})
    },
    [onRectChange],
  )

  const handlePointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  return (
    <div ref={containerRef} className="imc-crop-overlay">
      <div
        className="imc-crop-rect"
        style={{
          left: `${rect.x * 100}%`,
          top: `${rect.y * 100}%`,
          width: `${rect.w * 100}%`,
          height: `${rect.h * 100}%`,
        }}
        onPointerDown={beginDrag('move')}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {CORNERS.map((corner) => (
          // pointermove/pointerup bubble up to the rect div's handlers above once
          // captured — no need to attach them here too.
          <div key={corner} className={`imc-crop-handle imc-crop-handle-${corner}`} onPointerDown={beginDrag(corner)} />
        ))}
      </div>
    </div>
  )
}

export default ImageCropOverlay
